import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Roles } from "@/constants/roles";
import { getPrimaryRole } from "@/utils/roleUtils";
import { PageHead } from "@/components/PageHead";
import EnvironmentBadge from "@/components/EnvironmentBadge";
import fsvLogo from "@/images/achieveos-logo.png";
import fsvLogoDark from "@/images/achieveos-logo-dark.png";
import { UserProfileForm } from "./components/UserProfileForm";
import { MarketingPanel } from "./components/MarketingPanel";
import { StepProgress } from "@/components/StepProgress";
import { profileStepCompleted } from "@/utils/tracking";
import { initSessionReplay } from "@/utils/amplitude";
import {
  STEP2_PANEL_CONTENT,
  ADVISOR_STEP2_PANEL_CONTENT,
  PROGRAM_JOIN_STEP2_PANEL_CONTENT,
} from "@/constants/signupPanelContent";
import { ThemeToggle } from "@/components/ThemeToggle";
import { getSignupStepLabels } from "@/constants/signupSteps";
import { useToast } from "@/hooks/useToast";
import { api } from "@/services/api";
import { SIGNUP_WELCOME_MESSAGE } from "@/utils/toastMessages";

export const SignUpStepTwo = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const linkedInToastShownRef = useRef(false);

  const primaryRole = user ? getPrimaryRole(user) : null;
  const joinProgramId = localStorage.getItem("program_join_id") || undefined;
  // Achievers walk 3 steps; Experts 4 (Operator DNA, then S.T.A.R. Portfolio).
  // The join paths no longer collapse to 2.
  const stepLabels = getSignupStepLabels(primaryRole?.toUpperCase() === Roles.ADVISOR);

  useEffect(() => {
    initSessionReplay();
    if (primaryRole) {
      profileStepCompleted({ step_name: "profile_viewed", user_role: primaryRole });
    }
  }, [primaryRole]);

  // Welcome toast — shown once for every user who lands on step 2
  useEffect(() => {
    if (!user || linkedInToastShownRef.current) return;
    linkedInToastShownRef.current = true;
    showToast({
      type: "info",
      header: "Welcome to AchieveOS!",
      message: SIGNUP_WELCOME_MESSAGE,
      duration: 6000,
    });

    // Additionally, if this user came via LinkedIn, show a linked toast too
    const checkLinkedInNotification = async () => {
      try {
        const result = await api.notifications.list(1, 20);
        const linkedInNotification = result.items.find(
          (n) => n.notificationType === "linkedin_connected" && !n.isRead,
        );
        if (linkedInNotification) {
          showToast({
            type: "success",
            header: "LinkedIn Connected",
            message:
              "Your LinkedIn account has been linked successfully. We've pre-filled some of your profile details for you.",
            duration: 5000,
          });
          await api.notifications.markOneRead(linkedInNotification.id);
        }
      } catch {
        // Non-critical — ignore errors
      }
    };

    void checkLinkedInNotification();
  }, [user, showToast]);

  return (
    <>
      <PageHead
        title={`${primaryRole === "advisor" ? "Expert" : "Achiever"} Sign Up: Profile Setup`}
        description="Create your AchieveOS account"
      />
      <EnvironmentBadge />

      <div className="fixed inset-0 flex flex-col overflow-hidden font-inter bg-signup-form-bg bg-surface-page-bg">
        <div className="flex flex-1 min-h-0">
          {/* Left: form area */}
          <div className="flex-1 bg-signup-form-bg bg-surface-page-bg overflow-y-auto">
            <div className="flex flex-col items-center px-4 md:px-8 min-h-full py-8">
              <div className="w-full max-w-[620px] flex flex-col">
                {/* Logo + Theme Toggle */}
                <div className="flex items-center justify-between mb-10">
                  <img
                    src={fsvLogo}
                    alt="AchieveOS"
                    className="logo-light-mode h-[50px] w-[218px] max-w-full object-contain"
                  />
                  <img
                    src={fsvLogoDark}
                    alt="AchieveOS"
                    className="logo-dark-mode h-[50px] w-[218px] max-w-full object-contain"
                  />
                  <ThemeToggle />
                </div>

                <StepProgress
                  currentStep={2}
                  totalSteps={stepLabels.length}
                  stepLabels={stepLabels}
                />

                {/* Form card */}
                <div className="bg-theme-bg-surface border border-theme-border rounded-2xl px-4 md:px-10 pt-10 pb-10 mt-8">
                  <UserProfileForm joinProgramId={joinProgramId} allowOrgCode />
                </div>
              </div>
            </div>
          </div>

          {/* Right: marketing panel */}
          <div className="hidden md:block">
            <MarketingPanel
              content={
                primaryRole?.toUpperCase() === Roles.ADVISOR
                  ? ADVISOR_STEP2_PANEL_CONTENT
                  : joinProgramId
                    ? PROGRAM_JOIN_STEP2_PANEL_CONTENT
                    : STEP2_PANEL_CONTENT
              }
            />
          </div>
        </div>
      </div>
    </>
  );
};
